import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  ErrorResponse,
  getErrorMessages,
  isNumberedActivityUnavailable,
  isTradeCursorRejection,
  useDeleteTradeCommandMutation,
  useGetAccountActivityQuery,
  useLazyGetAccountActivityQuery,
} from "../../../../services";
import { ActivityEntry, TradeCommand } from "../../../../services/holdings";
import { PaginationType } from "../../../../services/types";
import { notify } from "../../../../components/Notification";
import { capitalizeFirstLetter } from "../../../../utils";
import { commandNoun, notifyLedgerCashWarnings } from "../../utils";
import {
  DEFAULT_ACTIVITY_REQUEST,
  toActivityQueryArgs,
} from "../../activityRequest";
import { UNVERSIONED_GENERATION } from "./useLoadGeneration";
import { useRetainedSection } from "./useRetainedSection";
import { UseAccountActivityProps, UseAccountActivityReturn } from "./types";

const LOAD_ERROR = "Failed to load account activity";

// Base-query + lazy-append accumulator, keyed on a cursor in cursor mode. Reads `currentData`, never
// `data` (see `UserContext`), so an in-flight `accountId` or filter switch can't render stale rows.
export const useAccountActivity = ({
  accountId,
  request = DEFAULT_ACTIVITY_REQUEST,
  skip = false,
  generation = UNVERSIONED_GENERATION,
  settlementAccountName,
}: UseAccountActivityProps): UseAccountActivityReturn => {
  const filterArgs = toActivityQueryArgs(accountId, request);
  const scopeKey = JSON.stringify(filterArgs);
  // The pair and generation are part of the key: a restart drops held pages and cursors as a set, as the policy requires.
  const freshArgs = {
    ...filterArgs,
    context: generation.context,
    generation: generation.generation,
  };
  const requestKey = JSON.stringify(freshArgs);
  // Keyed on the serialized args so a new `request` object each render doesn't churn `loadMore`.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const args = useMemo(() => freshArgs, [requestKey]);
  const base = useGetAccountActivityQuery(args, {
    skip: skip || !generation.isReady,
  });
  const {
    response,
    error: sectionError,
    refreshFailed,
    isRefreshing,
  } = useRetainedSection({
    query: base,
    section: "activity",
    scopeKey,
    generation,
  });
  const page = response?.result;
  const pageScopeKey = JSON.stringify({ ...filterArgs, page: undefined });
  const [fetchMore, lazyResult] = useLazyGetAccountActivityQuery();
  const [deleteTradeCommand] = useDeleteTradeCommandMutation();

  const [appended, setAppended] = useState<ActivityEntry[]>([]);
  const [heldPagination, setHeldPagination] = useState<{
    pageScopeKey: string;
    pagination: PaginationType;
  } | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const activePageScope = useRef({
    requestKey,
    fulfilledTimeStamp: base.fulfilledTimeStamp,
  });

  // Updated before the normal effect clears local pages, so a late-resolving lazy page is never appended to fresh data.
  useLayoutEffect(() => {
    activePageScope.current = {
      requestKey,
      fulfilledTimeStamp: base.fulfilledTimeStamp,
    };
  }, [requestKey, base.fulfilledTimeStamp]);

  // A cursor is only valid under the filters and pair that issued it, so a change drops it before "Load more" can replay it.
  useEffect(() => {
    setAppended([]);
    setNextCursor(base.currentData?.result.nextCursor ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base.fulfilledTimeStamp, requestKey]);

  const entries = useMemo(
    () => [...(page?.entries ?? []), ...appended],
    [page?.entries, appended],
  );

  // Depends on `refetch`, not `base`: the result object is a new identity every render, so the memo would never hold.
  const reload = useCallback(async () => {
    await base.refetch();
  }, [base.refetch]);

  const loadMore = useCallback(async () => {
    if (lazyResult.isFetching || !nextCursor || args.page !== undefined) {
      return;
    }
    const requestScope = {
      requestKey,
      fulfilledTimeStamp: base.fulfilledTimeStamp,
    };
    try {
      const continuation = await fetchMore({
        ...args,
        cursor: nextCursor,
      }).unwrap();
      if (
        activePageScope.current.requestKey !== requestScope.requestKey ||
        activePageScope.current.fulfilledTimeStamp !==
          requestScope.fulfilledTimeStamp
      ) {
        return;
      }
      setAppended((prev) => [...prev, ...continuation.result.entries]);
      setNextCursor(continuation.result.nextCursor);
    } catch (err: unknown) {
      // A stale continuation restarts the whole generation, activity from its first page included.
      if (generation.handleSectionError(err, args.generation)) {
        return;
      }
      // A refused cursor predates a release that reordered history; reload a fresh first page rather than
      // stranding it behind a "Load more" that can only fail (cleared only once that reload actually lands).
      if (isTradeCursorRejection(err)) {
        await reload();
        return;
      }
      // A lazy query bypasses the section's inline error state and the global middleware's toast, so name it here.
      notify.apiError(err as ErrorResponse);
    }
  }, [
    args,
    requestKey,
    base.fulfilledTimeStamp,
    generation.handleSectionError,
    fetchMore,
    lazyResult.isFetching,
    nextCursor,
    reload,
  ]);

  // Rethrows on failure, unlike `useInvestmentMarketUpdates.removeMarketUpdate` — the confirmation dialog
  // must stay open on rejection so a `TradeCorrectionConflictError` can be shown and the delete retried.
  const removeCommand = useCallback(
    async (command: TradeCommand, investmentId: number) => {
      const result = await deleteTradeCommand({
        commandId: command.commandId,
        investmentId,
        accountId,
        movesTransactions:
          settlementAccountName !== null || command.cashback !== null,
      }).unwrap();
      notify.success(`${capitalizeFirstLetter(commandNoun(command))} deleted`);
      // Removing the cash a command moved (e.g. a dividend credit) can leave the account short; the server
      // says so on the 200, raised here beside the success toast rather than instead of it.
      notifyLedgerCashWarnings(result, settlementAccountName);
    },
    [accountId, deleteTradeCommand, settlementAccountName],
  );

  // Held across a page flip under the same filters, so the pager (and the focus on the clicked page) survives the fetch.
  const livePagination = page?.pagination ?? null;
  if (livePagination && heldPagination?.pagination !== livePagination) {
    setHeldPagination({ pageScopeKey, pagination: livePagination });
  }
  const pagination =
    livePagination ??
    (args.page !== undefined && heldPagination?.pageScopeKey === pageScopeKey
      ? heldPagination.pagination
      : null);

  // Kept out of `error` so the section switches paging modes without flashing "Unavailable" first.
  const numberedUnavailable = isNumberedActivityUnavailable(base.error);
  const error =
    sectionError && !numberedUnavailable
      ? (getErrorMessages(sectionError as ErrorResponse)[0] ?? LOAD_ERROR)
      : null;
  const isAwaitingGeneration = !skip && !generation.isReady;

  return {
    entries,
    hasNext: nextCursor !== null && args.page === undefined,
    pagination,
    isLoading:
      page === undefined &&
      (base.isLoading || base.isFetching || isAwaitingGeneration),
    isLoadingMore: lazyResult.isFetching,
    error,
    refreshFailed: refreshFailed && !numberedUnavailable,
    isRefreshing,
    numberedUnavailable,
    freshness: response?.freshness ?? null,
    loadMore,
    reload,
    removeCommand,
  };
};
